// client/src/components/templates/EditTemplateModal.js
import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { X, Plus, Minus, Search, Package, Edit3, ShoppingBag, Save, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { orderTemplatesAPI } from '../../services/orderTemplatesAPI';
import axios from 'axios';
import { API_URL } from '../../config/api';
// ✅ FIX: Import helper
import { getImageUrl } from '../../utils/imageHelper';

const EditTemplateModal = ({ template, onClose, onSuccess }) => {
  const { t } = useTranslation(['business', 'common']);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [formData, setFormData] = useState({
    name: template.name || '',
    description: template.description || '',
    frequency: template.frequency || 'custom',
    items: []
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWholesalerId, setSelectedWholesalerId] = useState('all');
  const [wholesalers, setWholesalers] = useState([]);
  const [errors, setErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  
  const fetchedForTemplateRef = React.useRef(null);

  useEffect(() => {
    if (fetchedForTemplateRef.current === template._id) return;
    fetchedForTemplateRef.current = template._id;
    
    if (template.items) {
      const initialItems = template.items
        .filter(item => item.productId != null)
        .map(item => ({
          productId: item.productId._id || item.productId,
          wholesalerId: item.wholesalerId?._id || item.wholesalerId,
          wholesalerName: item.wholesalerId?.companyName || 'Unknown',
          productName: item.productId.name || item.name,
          price: item.productId.pricing?.unitPrice || item.productId.price || 0,
          unit: item.productId.pricing?.unit || item.productId.unit || 'piece',
          quantity: item.quantity,
          notes: item.notes || '',
          image: item.productId.images?.[0] || null,
          category: item.productId.category || '',
          hidePricing: item.wholesalerId?.hidePricing || false
        }));
      setFormData(prev => ({ ...prev, items: initialItems }));
    }

    fetchAllProducts();
    fetchWholesalers();
  }, [template._id]);

  useEffect(() => {
    if (selectedWholesalerId !== undefined) {
      fetchAllProducts(1, false);
    }
  }, [selectedWholesalerId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchAllProducts(1, false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchWholesalers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/wholesalers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWholesalers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching wholesalers:', error);
    }
  };

  const fetchAllProducts = async (page = 1, append = false) => {
    try {
      setLoadingProducts(true);
      const token = localStorage.getItem('token');
      
      let url = `${API_URL}/products?limit=100&page=${page}`;
      if (selectedWholesalerId && selectedWholesalerId !== 'all') {
        url += `&wholesalerId=${selectedWholesalerId}`;
      }
      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const productData = response.data?.data || response.data || [];
      const productsArray = Array.isArray(productData) ? productData : [];
      const pagination = response.data?.pagination || {};
      
      const activeProducts = productsArray.filter(p => p.status === 'active');
      
      if (append) {
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p._id));
          const newProducts = activeProducts.filter(p => !existingIds.has(p._id));
          return [...prev, ...newProducts];
        });
      } else {
        setProducts(activeProducts);
      }
      
      setCurrentPage(pagination.page || page);
      setTotalProducts(pagination.total || productsArray.length);
      setHasMore(pagination.page < pagination.pages);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (!append) setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const addProduct = (product) => {
    const existingItem = formData.items.find(item => item.productId === product._id);
    if (existingItem) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.productId === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: [
          ...prev.items,
          {
            productId: product._id,
            wholesalerId: product.wholesalerId?._id || product.wholesalerId,
            wholesalerName: product.wholesalerId?.companyName || 'Unknown',
            productName: product.name,
            price: product.pricing?.unitPrice || 0,
            unit: product.pricing?.unit || 'piece',
            quantity: 1,
            notes: '',
            image: product.images?.[0] || null,
            category: product.category || '',
            hidePricing: product.wholesalerId?.hidePricing || false
          }
        ]
      }));
    }
  };

  const removeProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.productId !== productId)
    }));
  };

  const loadMoreProducts = () => {
    if (!loadingProducts && hasMore) {
      fetchAllProducts(currentPage + 1, true);
    }
  };

  const updateItemQuantity = (productId, change) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.productId === productId
          ? { ...item, quantity: Math.max(1, item.quantity + change) }
          : item
      )
    }));
  };

  const updateItemNotes = (productId, notes) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.productId === productId ? { ...item, notes } : item
      )
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('business:templates.modal.nameRequired');
    if (formData.items.length === 0) newErrors.items = t('business:templates.modal.productsRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        frequency: formData.frequency,
        items: formData.items.map(item => ({
          productId: item.productId,
          wholesalerId: item.wholesalerId,
          quantity: item.quantity,
          notes: item.notes
        }))
      };

      await orderTemplatesAPI.update(template._id, updateData);
      toast.success(t('business:templates.modal.successUpdate'));
      onSuccess();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error(error.response?.data?.error || t('business:templates.modal.errorUpdate'));
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const notAlreadyAdded = !formData.items.some(item => item.productId === product._id);
      return notAlreadyAdded;
    });
  }, [products, formData.items]);

  const itemsByWholesaler = useMemo(() => {
    return formData.items.reduce((acc, item) => {
      const wholesalerId = item.wholesalerId;
      if (!acc[wholesalerId]) {
        acc[wholesalerId] = {
          name: item.wholesalerName,
          items: []
        };
      }
      acc[wholesalerId].items.push(item);
      return acc;
    }, {});
  }, [formData.items]);

  const totalValue = useMemo(() => {
    return formData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [formData.items]);
  
  const wholesalerCount = useMemo(() => {
    return Object.keys(itemsByWholesaler).length;
  }, [itemsByWholesaler]);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="relative bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-600 p-6 text-white">
          <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-0.5 sm:gap-1">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Edit3 className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">{t('business:templates.modal.editTitle')}</h2>
                <p className="text-orange-100 text-sm mt-1">{t('business:templates.modal.subtitle')}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 space-y-6">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 space-y-4 border border-orange-100">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('business:templates.templateName')} *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('common:templateModal.namePlaceholder')}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'}`}
                />
                {errors.name && <p className="mt-2 text-sm text-red-600 flex items-center gap-1"><span className="w-1 h-1 bg-red-600 rounded-full"></span>{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('business:templates.description')} ({t('common:optional')})</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={t('business:templates.modal.descriptionPlaceholder')}
                  rows={2}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white transition-all resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('business:templates.frequency')}</label>
                <select
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                >
                  <option value="custom">{t('business:templates.frequencyOptions.custom')}</option>
                  <option value="weekly">{t('business:templates.frequencyOptions.weekly')}</option>
                  <option value="biweekly">{t('business:templates.frequencyOptions.biweekly')}</option>
                  <option value="monthly">{t('business:templates.frequencyOptions.monthly')}</option>
                </select>
              </div>
            </div>

            <div>
              <div className="flex flex-col gap-2 mb-4">
                <h3 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2 mb-2">
                  <ShoppingBag className="w-5 h-5 text-orange-600" />
                  {t('business:templates.selectedProducts')}
                </h3>
                {formData.items.length > 0 && <div className="w-full px-3 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-semibold">📦 {t("business:templates.selectedProducts")}: {formData.items.length}</div>}
                {wholesalerCount > 0 && <div className="w-full px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-semibold flex items-center gap-2"><Store className="w-4 h-4" />{t("business:templates.wholesaler", { count: wholesalerCount })}: {wholesalerCount}</div>}
                {totalValue > 0 && !formData.items.some(i => i.hidePricing) && <div className="w-full px-3 py-2 bg-green-100 text-green-700 rounded-lg"><p className="text-xs text-green-600 mb-0.5">{t("business:templates.modal.estimatedTotal")}</p><p className="text-lg font-bold">€{totalValue.toFixed(2)}</p></div>}
              </div>

              {formData.items.length > 0 && (
                <div className="mb-4 space-y-4">
                  {Object.entries(itemsByWholesaler).map(([wholesalerId, group]) => (
                    <div key={wholesalerId} className="space-y-2">
                      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg border border-indigo-200">
                        <Store className="w-4 h-4 text-indigo-700" />
                        <span className="font-semibold text-indigo-900">{group.name}</span>
                        <span className="ml-auto text-sm text-indigo-700">{t('business:templates.items', { count: group.items.length })}</span>
                      </div>
                      {group.items.map(item => (
                        <div key={item.productId} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-2 sm:p-3 border-2 border-purple-200 hover:border-purple-300 transition-all ml-4">
                          <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3">
                            <div className="flex-shrink-0">
                              <img
                                src={getImageUrl(item.image)}
                                alt={item.productName}
                                className="w-12 h-12 sm:w-16 sm:h-16 sm:w-20 sm:h-20 object-cover rounded-lg border-2 border-white shadow-md"
                                onError={(e) => {
                                  if (!e.target.dataset.errorHandled) { e.target.dataset.errorHandled = 'true'; e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22100%22 height=%22100%22/%3E%3Cpath fill=%22%239ca3af%22 d=%22M30 35h40v5H30zm0 10h40v5H30zm0 10h25v5H30z%22/%3E%3Ccircle fill=%22%23d1d5db%22 cx=%2250%22 cy=%2265%22 r=%228%22/%3E%3C/svg%3E'; }
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm sm:text-base text-gray-900 mb-0.5">{item.productName}</p>
                              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                                {!item.hidePricing && <span className="px-2 py-0.5 bg-white rounded-md font-medium">€{item.price?.toFixed(2) || '0.00'} / {item.unit}</span>}
                                {item.category && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-xs font-medium">{item.category}</span>}
                              </div>
                              <input
                                type="text"
                                value={item.notes}
                                onChange={(e) => updateItemNotes(item.productId, e.target.value)}
                                placeholder={t("business:templates.modal.descriptionPlaceholder")}
                                className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-[10px] leading-tight sm:text-xs border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                              />
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mt-3 sm:mt-0 w-full sm:w-auto">
                            <div className="flex items-center gap-2 justify-center sm:justify-start">
                              <button type="button" onClick={() => updateItemQuantity(item.productId, -1)} className="p-1.5 sm:p-2 rounded-lg bg-white hover:bg-gray-100 border-2 border-gray-200 transition-colors"><Minus className="w-4 h-4 text-gray-600" /></button>
                              <div className="w-12 sm:w-14 text-center">
                                <span className="text-base sm:text-lg font-bold text-gray-900">{item.quantity}</span>
                                <p className="text-xs text-gray-500">{item.unit}</p>
                              </div>
                              <button type="button" onClick={() => updateItemQuantity(item.productId, 1)} className="p-1.5 sm:p-2 rounded-lg bg-white hover:bg-gray-100 border-2 border-gray-200 transition-colors"><Plus className="w-4 h-4 text-gray-600" /></button>
                            </div>
                            {!item.hidePricing && (
                            <div className="text-right min-w-[60px] sm:min-w-[80px]">
                              <p className="text-xs text-gray-500 mb-1">{t('common:templateModal.total')}</p>
                              <p className="text-sm sm:text-base font-bold text-purple-600">€{(item.price * item.quantity).toFixed(2)}</p>
                            </div>
                            )}
                            <button type="button" onClick={() => removeProduct(item.productId)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {errors.items && <p className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{errors.items}</p>}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div className="md:col-span-2 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder={t('business:templates.searchProducts')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white transition-all"
                  />
                </div>
                <select
                  value={selectedWholesalerId}
                  onChange={(e) => setSelectedWholesalerId(e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 bg-white rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                >
                  <option value="all">{t('business:templates.allWholesalers')}</option>
                  {wholesalers.map(w => (
                    <option key={w._id} value={w._id}>{w.companyName}</option>
                  ))}
                </select>
              </div>

              <div className="border-2 border-gray-200 rounded-xl max-h-80 overflow-y-auto bg-white">
                {loadingProducts ? (
                  <div className="p-8 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-2"></div>
                    {t('common:templateModal.loadingProducts')}
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">
                    <Package className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">{searchTerm || selectedWholesalerId !== 'all' ? t('common:templateModal.noProductsFound') : t('common:templateModal.allProductsAdded')}</p>
                    <p className="text-sm text-gray-400 mt-1">{searchTerm || selectedWholesalerId !== 'all' ? t('common:templateModal.tryDifferentSearch') : t('common:templateModal.allProductsAddedEmoji')}</p>
                  </div>
                ) : (
                  filteredProducts.map(product => (
                    <button
                      key={product._id}
                      type="button"
                      onClick={() => addProduct(product)}
                      className="w-full p-4 hover:bg-orange-50 flex items-center gap-4 border-b border-gray-100 last:border-b-0 text-left transition-all group"
                    >
                      <div className="flex-shrink-0">
                        <img
                          src={getImageUrl(product.images?.[0])}
                          alt={product.name}
                          className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-lg border-2 border-gray-200 group-hover:border-orange-300 transition-colors"
                          onError={(e) => {
                            if (!e.target.dataset.errorHandled) { e.target.dataset.errorHandled = 'true'; e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22100%22 height=%22100%22/%3E%3Cpath fill=%22%239ca3af%22 d=%22M30 35h40v5H30zm0 10h40v5H30zm0 10h25v5H30z%22/%3E%3Ccircle fill=%22%23d1d5db%22 cx=%2250%22 cy=%2265%22 r=%228%22/%3E%3C/svg%3E'; }
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{product.name}</p>
                        {!product.wholesalerId?.hidePricing && <p className="text-sm text-gray-600 mt-1">€{product.pricing?.unitPrice?.toFixed(2) || '0.00'} / {product.pricing?.unit || 'piece'}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          {product.category && <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{product.category}</span>}
                          <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-1"><Store className="w-3 h-3" />{product.wholesalerId?.companyName || 'Unknown'}</span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="p-2 bg-orange-600 text-white rounded-lg group-hover:bg-orange-700 transition-colors"><Plus className="w-5 h-5" /></div>
                      </div>
                    </button>
                  ))
                )}
              </div>
              
              {hasMore && !loadingProducts && (
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                  <button type="button" onClick={loadMoreProducts} className="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"><Package className="w-4 h-4" />{t('common:templateModal.loadMoreProducts')} ({t('common:templateModal.remaining', { count: totalProducts - products.length })})</button>
                </div>
              )}
              {loadingProducts && currentPage > 1 && (
                <div className="p-4 border-t border-gray-200 bg-gray-50 text-center">
                  <div className="inline-flex items-center gap-2 text-gray-600"><div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>{t('common:templateModal.loadingMoreProducts')}</div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 sm:p-6 border-t-2 border-gray-100 bg-gradient-to-r from-gray-50 to-orange-50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600">{t('business:templates.items', { count: formData.items.length })} {t('common:from')} {wholesalerCount} {t('business:templates.wholesaler', { count: wholesalerCount })}{!formData.items.some(i => i.hidePricing) ? ` • ${t('business:templates.modal.total')}: €${totalValue.toFixed(2)}` : ''}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-semibold transition-all">{t('common:buttons.cancel')}</button>
              <button type="submit" disabled={loading} className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl hover:from-orange-700 hover:to-amber-700 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-2"><Save className="w-5 h-5" />{loading ? t('business:templates.modal.saving') : t('business:templates.modal.updateTemplate')}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTemplateModal;